/**
 * The owner's desk, read as a conversation.
 *
 * A command is one side of an exchange and what came back of it is the other,
 * and until now they lived on different screens. This pairs them: every command
 * the owner sent, oldest first, each followed by the plan drafted from it - or,
 * when the owner asked rather than told, by the answer.
 *
 * Pure - the caller passes the rows, the answers and the clock in.
 */
import { agoLabel } from './runs';
import { DISK_MIN_FREE_BYTES, DISK_MIN_FREE_PERCENT, roadmapReadQuestion } from '@brunel/shared';
import type { HeldDetail, OwnerCommand, OwnerCommandControl, Plan, Project, RunningTask, Worker, WorkerChoice } from './data';
import type { PlanOutcome } from './outcome';
import { wasAnswered } from './plans';
import { proposalArchiveTranscript, type ProposalArchiveReason } from './proposal-archive';
import { planRouting, type RoutingScope, type WorkerRouteTarget, type WorkerSeat } from './plan-routing';
import { persistentRuntimeLabel } from './persistent-ai-session-view';
import {
  ANSWERED_NO_WORK_TEXT,
  NO_WORK_CREATED_TEXT,
  latestPlanForCommand,
  ownerCommandPhase,
  ownerCommandStatusLabel,
  TARGET_ANSWER_REASONS,
} from './owner-command-status';

/**
 * The rest of the desk, as far as a turn needs it: enough to say WHERE a plan
 * would run before it is approved. Passing it is optional - a transcript drawn
 * without it says nothing about where rather than making something up.
 */
export interface DeskContext {
  readonly projects: readonly Project[];
  readonly workers: readonly Worker[];
  readonly running: readonly RunningTask[];
  /** #564: exact per-Worker routing truth. When present, "Runs on" follows the server's pins. */
  readonly routes?: readonly WorkerRouteTarget[];
  /** #564: standing "use this Worker only" scopes per project. */
  readonly scopes?: readonly RoutingScope[];
  /** Each Worker's open session per project and the AIs its computer has, for a held draft the server named no choice for. */
  readonly seats?: readonly WorkerSeat[];
}

/** What the transcript knows beyond the commands and the plans themselves. */
export interface ThreadDesk {
  readonly context: DeskContext | null;
  /** What became of each approved plan, by plan id. */
  readonly outcomes: ReadonlyMap<string, PlanOutcome>;
  /** #644 Slice C: the control receipt of an owner message that was an exact existing control, by command id. */
  readonly controls?: ReadonlyMap<string, OwnerCommandControl>;
  /**
   * #709: owner_commands Roadmap Autopilot wrote for a claimed roadmap item (roadmap_program_id set).
   * They stay in the transcript for history, but are never shown as something the owner typed.
   */
  readonly generated?: ReadonlySet<string>;
  /**
   * #644: commands the Overseer answers itself (a reply given, claimed or queued). Their answer
   * is the Overseer's own message in the timeline, never the screen's computed line.
   */
  readonly overseerReplies?: ReadonlySet<string>;
  /**
   * Proposed plans older than 48 hours whose work finished or was replaced.
   * The turn stays in the thread and no longer offers a decision.
   */
  readonly archivedPlans?: ReadonlyMap<string, ProposalArchiveReason>;
}

/** #644 Slice C: the chip on a control result. "Requested" never claims the effect already happened. */
export function controlStatusLabel(state: OwnerCommandControl['state']): string {
  switch (state) {
    case 'completed': return 'DONE';
    case 'accepted': return 'REQUESTED';
    case 'clarification': return 'NEEDS YOUR ANSWER';
    case 'refused': return 'NOT DONE';
  }
}

const NOTHING_KNOWN: ThreadDesk = { context: null, outcomes: new Map() };

export type CommandTurnKind = 'command' | 'plan' | 'note' | 'answer';

export interface CommandTurn {
  readonly id: string;
  readonly kind: CommandTurnKind;
  /** The owner's side of the exchange sits on the owner's side of the screen. */
  readonly mine: boolean;
  /** What is read in the bubble. */
  readonly text: string;
  /** The command's own number, for the owner's turns. */
  readonly seq: number | null;
  /** A plan's state, for the turns that carry one. */
  readonly status: Plan['status'] | null;
  /** What the planner said when it could not draft, otherwise null. */
  readonly reason: string | null;
  /** How many tasks the plan holds, when it holds any. */
  readonly tasks: number | null;
  /** "just now", "12 min ago" - empty when the row carries no usable time. */
  readonly atLabel: string;
  /**
   * #651: the durable time of the record this turn shows - owner_commands.created_at for
   * the owner's message, plans.created_at for a plan or the Overseer's recorded answer.
   * Null or absent when that record carries none (an owner row older than the column, an
   * answer composed on screen): unknown, never inferred from seq or neighbouring replies.
   */
  readonly sentAt?: string | null;
  /**
   * What this turn puts in front of the owner to decide, or null when there is
   * nothing left to decide on it. Only a plan still waiting carries one.
   */
  readonly offer: PlanDecisionOffer | null;
  /**
   * What came of the plan this turn carries, once the owner approved it. Null
   * on every other turn, and null on an approved plan whose runs cannot be
   * identified from the rows in hand.
   */
  readonly outcome: PlanOutcome | null;
  /**
   * Owner-visible phase label derived from the latest plan (QUEUED, ANSWERED,
   * FAILED, NO WORK CREATED, …). Null when the turn does not carry one.
   */
  readonly statusLabel: string | null;
  /** Exact Main Command management roles selected when this owner message was persisted. */
  readonly replyTargets?: readonly string[] | null;
  /** Stable Worker ids selected through the normalized Main Command recipient table. */
  readonly workerTargets?: readonly string[] | null;
  /** #709: set when the command was written by Roadmap Autopilot, not typed by the owner. */
  readonly generatedBy?: 'roadmap_autopilot' | null;
}

/** The decisions the guarded plan write takes from the owner in the transcript. */
export type PlanDecision = 'approve' | 'reject';

/** One step of a plan, readable where it stands before anything is decided. */
export interface TurnTask {
  readonly title: string;
  readonly instructions: string;
}

/**
 * What a turn offers: the plan it carries, in enough detail to be read, and the
 * decisions still open on it. The decision itself stays the owner's - this only
 * says what he is being asked, and where.
 */
export interface PlanDecisionOffer {
  readonly planId: string;
  readonly orgId: string;
  /** One line: what the plan is for. */
  readonly summary: string;
  /** "3 tasks", or plain words when the plan holds none. */
  readonly tasksLabel: string;
  readonly tasks: readonly TurnTask[];
  /** Which project it runs in and who would pick it up, or null when neither is known. */
  readonly where: string | null;
  readonly decisions: readonly PlanDecision[];
  /** #656: what each decision means for a held instruction; absent, DECISION_LABEL. */
  readonly labels?: Readonly<Partial<Record<PlanDecision, string>>>;
  /** Said beside the drop a held draft offers: what dropping the request does. */
  readonly note?: string;
  /** Said beside Try again, on a held draft a second check can clear: what trying again does. */
  readonly retryNote?: string;
  /** A Worker or AI question held on this plan: the Workers, each with its AI, the owner can choose to go ahead with. */
  readonly choices?: readonly WorkerChoice[];
  /** Said above those choices when they need more than the usual line. */
  readonly choicePrompt?: string;
}

/** What each decision is called where the owner reads it. */
export const DECISION_LABEL: Readonly<Record<PlanDecision, string>> = {
  approve: 'Approve plan',
  reject: 'Reject',
};

/** Said instead of an empty list, so nothing is approved that reads as blank. */
export const NO_TASKS_LABEL = 'This plan lists no tasks.';

/** Said when the buttons on screen were drawn before someone else decided. */
export const STALE_DECISION_NOTICE =
  'This plan is no longer waiting on you - it was decided somewhere else. Nothing was sent.';

/**
 * Whether a plan is still the owner's to decide, and what he reads first.
 * Only a proposed plan is: approved, rejected and failed plans are history, and
 * the transcript offers nothing on them. A plan with no tasks says so in words
 * rather than showing an empty list to approve blind.
 */
export function planDecisionOffer(plan: Plan, context: DeskContext | null = null, commandContent: string | null = null): PlanDecisionOffer | null {
  if (plan.status !== 'proposed') return null;
  const tasks: TurnTask[] = (Array.isArray(plan.tasks) ? plan.tasks : [])
    .map((task) => ({ title: task.title, instructions: task.instructions }));
  return {
    planId: plan.id,
    orgId: plan.org_id,
    summary: plan.summary === '' ? 'Proposed plan' : plan.summary,
    tasksLabel: tasks.length === 0 ? NO_TASKS_LABEL : tasks.length === 1 ? '1 task' : `${tasks.length} tasks`,
    tasks,
    where: whereItRuns(plan, context, commandContent),
    ...heldDecisions(plan),
    ...heldChoices(plan, context),
  };
}

/**
 * The Workers a held Worker or AI question can be answered with: the ones the server named,
 * and, for an AI no Worker is set up with, the ones the desk shows can still run it.
 */
function heldChoices(plan: Plan, context: DeskContext | null): Pick<PlanDecisionOffer, 'choices' | 'choicePrompt'> {
  const held = plan.held_for ?? null;
  if (held === null || !TARGET_ANSWER_REASONS.has(held)) return {};
  const named = plan.held_detail?.choices ?? [];
  if (named.length > 0) return { choices: named };
  const seated = seatChoices(plan, context);
  if (seated.length === 0) return {};
  const ai = seated[0]!.runtime ?? 'that AI';
  return {
    choices: seated,
    choicePrompt: seated.length === 1
      ? `This Worker is on a computer that has ${ai}. Choose it and the work goes ahead there with ${ai}:`
      : `These Workers are on a computer that has ${ai}. Choose one and the work goes ahead there with ${ai}:`,
  };
}

/** As many choices as the server itself offers at once. */
const MAX_CHOICES = 12;

/**
 * No Worker in the project is set up with the AI the owner named, so the server has no Worker
 * to name. It still accepts a Worker the owner chooses whose computer has that AI, and runs
 * the work there with it. Those Workers are read off the desk: an open session in the plan's
 * project, on a computer with that AI signed in (and the computer the owner named, if any).
 * The server checks the chosen Worker again, and no other AI is used in its place.
 */
function seatChoices(plan: Plan, context: DeskContext | null): readonly WorkerChoice[] {
  const detail = plan.held_detail ?? {};
  const runtime = detail.runtime;
  if (plan.held_for !== 'no_eligible_worker' || runtime === undefined || context?.seats === undefined) return [];
  // One choice cannot answer a draft that already names several Workers.
  if (draftWorkerNumbers(plan).length > 1) return [];
  return context.seats
    .filter((seat) => seat.projectId === plan.project_id && seat.runtimeKeys.includes(runtime)
      && (detail.machine === undefined || seat.nodeName === detail.machine))
    .slice(0, MAX_CHOICES)
    .map((seat) => ({
      workerNumber: seat.workerNumber,
      name: seat.workerName,
      ...(seat.nodeName === null ? {} : { machine: seat.nodeName }),
      runtime: persistentRuntimeLabel(runtime),
    }));
}

/** The Workers a draft's own tasks name. */
function draftWorkerNumbers(plan: Plan): readonly number[] {
  const tasks = Array.isArray(plan.tasks) ? plan.tasks : [];
  return [...new Set(tasks.map((task) => task.preferredWorkerNumber).filter((n): n is number => typeof n === 'number'))];
}

/** Choices that share an AI, in the order the choices came. The AI is null when a choice names none. */
export interface ChoiceGroup {
  readonly ai: string | null;
  readonly choices: readonly WorkerChoice[];
}

/**
 * The choices sorted under the AI each would use, so the owner picks the AI and then the
 * Worker. One group when they all use the same AI.
 */
export function choiceGroups(choices: readonly WorkerChoice[]): readonly ChoiceGroup[] {
  const groups: { ai: string | null; choices: WorkerChoice[] }[] = [];
  for (const choice of choices) {
    const ai = choice.runtime ?? null;
    const group = groups.find((entry) => entry.ai === ai);
    if (group) group.choices.push(choice);
    else groups.push({ ai, choices: [choice] });
  }
  return groups;
}

/** How one Worker choice reads on its button: "Worker 7 · Grok Build on Windows PC". */
export function workerChoiceLabel(choice: WorkerChoice): string {
  const name = `Worker ${choice.workerNumber}`;
  const named = choice.name !== undefined && choice.name !== name ? ` (${choice.name})` : '';
  const where = [choice.runtime, choice.machine === undefined ? undefined : `on ${choice.machine}`].filter((part) => part !== undefined).join(' ');
  return where === '' ? `${name}${named}` : `${name}${named} · ${where}`;
}

/** What the one decision a held draft leaves is called. */
export const DROP_LABEL = 'Drop this request';

/** Said beside it: what dropping does, so the button is never the whole explanation. */
export const DROP_NOTE = 'Dropping closes this draft for good: nothing runs from it, and you would have to ask again to get it done.';

/** Said above the choices that answer a held draft. Each one is the go-ahead. */
export const CHOICE_PROMPT = 'Choose one and it goes ahead:';

/** Said instead when the choices use different AIs: the owner picks the AI, then the Worker under it. */
export const CHOICE_PROMPT_AI = 'Choose the AI and the Worker, and it goes ahead:';

/** The line above a set of choices: which of the two prompts fits them. */
export function choicePromptFor(choices: readonly WorkerChoice[]): string {
  return choiceGroups(choices).length > 1 ? CHOICE_PROMPT_AI : CHOICE_PROMPT;
}

/** How a choice reads under its AI's heading, where the AI is already said. */
export function workerChoiceLabelUnderAi(choice: WorkerChoice): string {
  const name = `Worker ${choice.workerNumber}`;
  const named = choice.name !== undefined && choice.name !== name ? ` (${choice.name})` : '';
  return `${name}${named}${choice.machine === undefined ? '' : ` on ${choice.machine}`}`;
}

/** What checking a held draft again is called. */
export const RETRY_LABEL = 'Try again';

/** Said beside it: what trying again does, and that it can come back the same. */
export const RETRY_NOTE = 'Trying again checks this same draft once more: it goes ahead if the AI it names can be used now, and stays held if not.';

/**
 * The only holds that still ask for a go-ahead: Settings -> plan approval is on, or the
 * instruction did not come from the owner. An owner's own command is the approval, and a busy
 * or offline Worker queues it on the server, so no other hold offers Go ahead or Queue it.
 *
 * #656: every other hold cannot be approved from the draft as it stands - the target the owner
 * named is not one Brunel can use, or (#805) the instruction is a copy of work already open. The
 * draft can be answered with a choice when there is one (heldChoices), checked again when a
 * second check can clear it (canRetry), and otherwise only dropped, which is said in those
 * words with what it does beside it.
 */
const GO_AHEAD = new Set(['plan_approval', 'command_author_not_owner']);

/**
 * Holds a second check can clear, because what they wait on is set up outside the draft: a
 * Worker given the AI, the AI signed in, or the Worker set to one of its setups. The check is
 * the same guarded approval, with no Worker chosen. A message that names several AIs is not
 * one of them: the message is the question, and checking again asks it again.
 */
function canRetry(held: string, detail: HeldDetail): boolean {
  if (held === 'no_eligible_worker') return true;
  return held === 'ambiguous_runtime_target' && detail.runtime !== undefined;
}

function heldDecisions(plan: Plan): Pick<PlanDecisionOffer, 'decisions' | 'labels' | 'note' | 'retryNote'> {
  const held = plan.held_for ?? null;
  if (held === null) return { decisions: ['approve', 'reject'] };
  if (GO_AHEAD.has(held)) return { decisions: ['approve', 'reject'], labels: { approve: 'Go ahead', reject: 'Dismiss' } };
  if (canRetry(held, plan.held_detail ?? {})) {
    return { decisions: ['approve', 'reject'], labels: { approve: RETRY_LABEL, reject: DROP_LABEL }, note: DROP_NOTE, retryNote: RETRY_NOTE };
  }
  return { decisions: ['reject'], labels: { reject: DROP_LABEL }, note: DROP_NOTE };
}

/** Said beside an approved plan whose work is queued: accepted, and nothing has started yet. */
export const APPROVED_QUEUED_LABEL = 'approved · queued';

/** True when every task of an approved plan is still waiting to be picked up. */
function onlyQueued(outcome: PlanOutcome | null): boolean {
  return outcome !== null && outcome.tasks.length > 0 && outcome.tasks.every((task) => task.state === 'waiting');
}

/** Said when every worker already has something running. */
export const NO_WORKER_FREE = 'it waits for a worker to come free';
/** Said when the organization has no workers at all yet. */
export const NO_WORKERS_YET = 'no worker is set up yet';

/**
 * Where an approved plan would go: the project it belongs to, and who would
 * pick it up. The worker is said as what is TRUE now - which workers have
 * nothing running - not as a promise about which one the dispatcher will
 * choose, because that is not this screen's to make. Null when the caller
 * passed no desk to read: an unknown is left unsaid.
 */
export function whereItRuns(plan: Plan, context: DeskContext | null, commandContent: string | null = null): string | null {
  if (context === null) return null;
  const project = context.projects.find((p) => p.id === plan.project_id)?.name ?? null;
  if (context.routes !== undefined) {
    // #564: the exact Worker, computer and runtime the server will pin, or a truthful block.
    const routing = planRouting({
      plan, commandContent, targets: context.routes, scopes: context.scopes ?? [],
      runningWorkerNumbers: new Set(context.running.map((r) => r.worker_number).filter((n): n is number => n !== null)),
    });
    return project === null ? routing.text : `In ${project}: ${routing.text}`;
  }
  const busy = new Set(context.running.map((r) => r.worker_number));
  const free = context.workers.filter((w) => !busy.has(w.number)).map((w) => w.number).sort((a, b) => a - b);
  const who = context.workers.length === 0
    ? NO_WORKERS_YET
    : free.length === 0
      ? NO_WORKER_FREE
      : `the first free worker (${free.length === 1 ? `Worker ${free[0]}` : `Worker ${free[0]}, of ${free.length} free`})`;
  return project === null ? `Runs on ${who}.` : `Runs in ${project}, on ${who}.`;
}

/** What the turn says once the owner has decided, in place of the buttons. */
export function decidedNotice(decision: PlanDecision): string {
  return decision === 'approve'
    ? 'Approved. It is queued until a Worker picks it up.'
    : 'Turned down. Nothing runs from this plan.';
}

/**
 * The offer as the newest reading of the desk has it. A plan decided in another
 * tab between the turn being drawn and the button being pressed is no longer
 * offered here, and the caller must send nothing: that button is stale, not a
 * second decision to make.
 */
export function offerStillOpen(turns: readonly CommandTurn[], planId: string): PlanDecisionOffer | null {
  for (const turn of turns) {
    if (turn.offer !== null && turn.offer.planId === planId) return turn.offer;
  }
  return null;
}

function planLine(plan: Plan, nowMs: number, offered: boolean): string {
  const count = Array.isArray(plan.tasks) ? plan.tasks.length : 0;
  const work = count === 1 ? '1 task' : `${count} tasks`;
  switch (plan.status) {
    case 'proposed': {
      const held = heldLine(plan, nowMs, offered);
      if (held !== null) return `${plan.summary === '' ? 'Not started' : `${plan.summary} - not started`}: ${held}`;
      return plan.summary === '' ? `Drafted a plan of ${work}. Waiting on you.` : `${plan.summary} - ${work}. Waiting on you.`;
    }
    case 'failed': {
      const summary = plan.summary.trim();
      return summary === '' ? 'Could not draft a plan for this.' : summary;
    }
    case 'rejected':
      return plan.summary === '' ? `Plan of ${work} - dismissed, nothing runs from it.` : `${plan.summary} - dismissed, nothing runs from it.`;
    default:
      // What came of it is said underneath, by the outcome, which is the only
      // thing that actually knows - this line says only that it was accepted.
      // #644: accepting is not starting; only an assignment and a run are.
      if (plan.authority === 'owner_command') {
        return plan.summary === '' ? `Accepted ${work} on your instruction.` : `${plan.summary} - ${work}, accepted on your instruction.`;
      }
      return plan.summary === '' ? `Plan of ${work} - you approved it.` : `${plan.summary} - ${work}, approved.`;
  }
}

/**
 * #644: a direct instruction the command alone could not carry out, said as the one
 * question it raises - never as "approve my plan" for what the owner just ordered.
 * `offered` is whether the turn shows choices under this line, so it never promises one that
 * is not there.
 */
function heldLine(plan: Plan, nowMs: number, offered: boolean): string | null {
  const reason = plan.held_for ?? null;
  const detail = plan.held_detail ?? {};
  const worker = heldWorker(plan);
  const computer = `${worker}'s computer${detail.machine === undefined ? '' : ` (${detail.machine})`}`;
  // A hold recorded before owner commands queued themselves. Nothing was queued from it.
  const wait = 'This draft was held, not queued.';
  // A question about who does it: answered with a choice when one is offered, and always
  // answerable in words. What trying again and dropping do is said beside their buttons.
  const answer = (reply: string) => (offered ? `Choose one below and it goes ahead, or reply below with ${reply}` : `Reply below with ${reply}`);
  const namedWorker = '“Worker N should do this.”';
  switch (reason) {
    case null: return null;
    case 'protected_action':
    case 'plan_first':
      return 'it was held for a go-ahead before your own commands counted as the approval. Nothing was queued from this draft.';
    case 'plan_approval': return 'plan approval is on in Settings, so it waits for your go-ahead. Go ahead?';
    case 'duplicate_command': return 'you already sent this exact instruction and its work is still running. This copy was not started.';
    case 'worker_target_conflict': return `the work drafted for it names a different Worker than you did. ${answer(namedWorker)}`;
    case 'worker_retired': return `the Worker it names is retired. ${answer(namedWorker)}`;
    case 'not_found': return `the Worker it names does not exist here. ${answer(namedWorker)}`;
    case 'ambiguous_worker_target': return `Brunel could not tell which Worker should do this. ${answer(namedWorker)}`;
    // The server records the AI by its key here; it is said by the name the choices use.
    case 'no_eligible_worker': return detail.limitation === 'metered_only'
      ? `the signed-in ${detail.runtime === undefined ? 'AI' : persistentRuntimeLabel(detail.runtime)} runtime is not available here${detail.machine === undefined ? '' : ` on ${detail.machine}`}. The metered route stays unused. ${answer('another Worker or AI.')}`
      : `no Worker with an open session in this project is set up with ${detail.runtime === undefined ? 'the AI you named' : persistentRuntimeLabel(detail.runtime)}${detail.machine === undefined ? '' : ` on ${detail.machine}`}. ${answer('another Worker or AI.')}`;
    case 'ambiguous_scope': return 'this draft names several Workers you did not name. Reply below with which Worker should do each part.';
    // The server records the AI and computer only when one named AI has several setups there;
    // with neither recorded, the command itself named more than one AI.
    case 'ambiguous_runtime_target': return detail.runtime === undefined
      ? `your message names more than one AI, so Brunel could not tell which one to use. ${answer('the one AI to use.')}`
      : `${detail.runtime} is set up in more than one way on ${computer} and ${worker} is not set to use it, so Brunel could not tell which to use. ${answer('the Worker or AI to use.')}`;
    case 'machine_mismatch': return `${worker} is not on the computer you named. ${answer(namedWorker)}`;
    case 'command_author_not_owner': return 'the instruction did not come from the owner, so it needs the owner\'s go-ahead.';
    // #656: the exact target was not ready. Nothing was created; nothing was sent elsewhere.
    case 'dispatch_paused': return `dispatch is paused for this project. ${wait}`;
    case 'no_budget_policy': return `this project has no budget set. ${wait}`;
    case 'budget_exhausted': return `this project's budget is used up. ${wait}`;
    case 'worker_offline': return `${worker} is offline. ${wait}`;
    case 'worker_busy': return `${worker} is busy with other work. ${wait}`;
    case 'worker_not_ready': return `${worker} has no open session in this project. ${wait}`;
    case 'machine_offline': return `${computer} is offline. ${wait}`;
    case 'machine_held': return `${computer} is not taking work right now: ${holdingBackLine(detail)}. ${wait}`;
    case 'workspace_not_bound': return `${computer} does not have this project's workspace. ${wait}`;
    case 'runtime_not_on_machine': return `${detail.runtime ?? 'the AI you named'} is not set up on ${computer}. ${answer('another Worker or AI.')}`;
    case 'runtime_unavailable':
      return `${detail.runtime ?? 'the AI it needs'} on ${computer} is ${outageLine(detail.outage)}${untilLine(detail.availableFrom, nowMs)}. ${wait}`;
    default: return `it needs your go-ahead (${reason}).`;
  }
}

/** What the computer said about the runtime, in words. */
function outageLine(outage: HeldDetail['outage']): string {
  switch (outage) {
    case 'out_of_usage': return 'out of usage';
    case 'signed_out': return 'signed out';
    case 'misconfigured': return 'misconfigured';
    default: return 'out of usage or signed out';
  }
}

/** When the runtime said to try again, from now; nothing when it did not say. */
function untilLine(availableFrom: string | undefined, nowMs: number): string {
  const ms = availableFrom === undefined ? Number.NaN : Date.parse(availableFrom) - nowMs;
  if (Number.isNaN(ms) || ms <= 0) return '';
  const minutes = Math.max(1, Math.round(ms / 60_000));
  return minutes < 90 ? ` for about ${minutes} more minute${minutes === 1 ? '' : 's'}` : ` for about ${Math.round(minutes / 60)} more hours`;
}

/** What holds the computer back, as the headroom check found it. */
function holdingBackLine(detail: HeldDetail): string {
  switch (detail.holdingBack) {
    case 'seats': return 'it already runs as many Workers as you allowed';
    case 'memory': return 'its free memory is below your reserve';
    case 'video_memory': return 'its free graphics memory is below your reserve';
    case 'cpu': return 'it is under sustained CPU load';
    case 'graphics': return 'it is under sustained GPU load';
    case 'runtime_memory': return `it does not have the memory ${detail.runtime ?? 'that AI'} usually needs`;
    // Brunel's own floor rather than a reserve the owner set, so it is said as that.
    case 'disk': return `its free disk space is under the ${DISK_MIN_FREE_BYTES / (1024 ** 3)} GB and ${DISK_MIN_FREE_PERCENT}% Brunel keeps free`;
    default: return 'it is held back';
  }
}

/** The Worker a held instruction names, in the words the owner uses. */
function heldWorker(plan: Plan): string {
  if (typeof plan.held_detail?.workerNumber === 'number') return `Worker ${plan.held_detail.workerNumber}`;
  const numbers = draftWorkerNumbers(plan);
  return numbers.length === 1 ? `Worker ${numbers[0]}` : 'the Worker';
}

function noWorkLine(plan: Plan): string {
  const summary = plan.summary.trim();
  return summary === '' ? NO_WORK_CREATED_TEXT : `${summary} — ${NO_WORK_CREATED_TEXT.toLowerCase()}`;
}

function whenLabel(iso: string, nowMs: number): string {
  const ms = Date.parse(iso);
  return Number.isNaN(ms) ? '' : agoLabel(nowMs - ms);
}

function blankTurn(): Pick<CommandTurn, 'seq' | 'status' | 'reason' | 'tasks' | 'atLabel' | 'sentAt' | 'offer' | 'outcome' | 'statusLabel'> {
  return {
    seq: null, status: null, reason: null, tasks: null, atLabel: '', sentAt: null,
    offer: null, outcome: null, statusLabel: null,
  };
}

/**
 * Every command the owner sent, oldest first, each followed by what came back.
 *
 * A command that ASKED is followed by its answer. The planner records those as
 * answered rather than drafting for them, and that bookkeeping row is never
 * shown as a plan - an answered question is not a plan that failed. Where the
 * planner managed to WRITE the answer it left it on that same row, as its
 * summary, and those words are what the owner reads. Where it did not, the
 * caller's own answer, composed from the rows on screen, is shown instead - and
 * when neither is present the owner still gets a terminal "Answered — no work
 * created." rather than a permanent QUEUED.
 *
 * A command that was organization-wide asks for no plan at all, and the thread
 * says so rather than leaving a turn hanging with no answer. Plans whose command
 * is no longer in view are left out: they are not orphans, they are in Plan
 * history with the words that started them.
 */
export function commandThread(
  commands: readonly OwnerCommand[], plans: readonly Plan[], nowMs: number,
  answers: ReadonlyMap<string, string> = new Map(),
  desk: ThreadDesk = NOTHING_KNOWN,
): CommandTurn[] {
  const byCommand = new Map<string, Plan[]>();
  for (const plan of plans) {
    const held = byCommand.get(plan.command_id);
    if (held) held.push(plan);
    else byCommand.set(plan.command_id, [plan]);
  }

  const turns: CommandTurn[] = [];
  for (const command of [...commands].sort((a, b) => a.seq - b.seq)) {
    const commandPlans = byCommand.get(command.id) ?? [];
    const phase = ownerCommandPhase(command, commandPlans);
    const latest = latestPlanForCommand(command.id, commandPlans);
    const targetsOverseer = (command.reply_targets ?? ['overseer']).includes('overseer');
    const generated = desk.generated?.has(command.id) === true;

    turns.push({
      id: command.id, kind: 'command', mine: !generated, text: command.content,
      generatedBy: generated ? 'roadmap_autopilot' : null,
      seq: command.seq, status: null, reason: null, tasks: null, atLabel: '',
      sentAt: command.created_at ?? null,
      offer: null, outcome: null,
      replyTargets: command.reply_targets ?? ['overseer'],
      workerTargets: command.worker_reply_targets ?? [],
      // QUEUED only while a project command truly has no plan yet.
      statusLabel: phase === 'queued' && command.project_id !== null
        ? ownerCommandStatusLabel('queued')
        : null,
    });

    // #644 Slice C: an exact control was carried out (or asked about) at once. Its result
    // is the answer under the owner's message - never a plan card, a note or QUEUED.
    const control = desk.controls?.get(command.id);
    if (control !== undefined) {
      turns.push({
        id: `${command.id}:control`, kind: 'answer', mine: false, text: control.summary,
        ...blankTurn(),
        atLabel: whenLabel(control.created_at, nowMs),
        // #651: the receipt's own durable time.
        sentAt: control.created_at,
        statusLabel: controlStatusLabel(control.state),
      });
      continue;
    }

    if (phase === 'answered' && latest !== null) {
      const written = latest.summary.trim();
      const canonicalRoadmap = roadmapReadQuestion(command.content) !== null
        ? answers.get(command.id) : undefined;
      const text = canonicalRoadmap ?? (written !== ''
        ? written
        : (answers.get(command.id) ?? ANSWERED_NO_WORK_TEXT));
      turns.push({
        id: `${command.id}:answer`, kind: 'answer', mine: false, text,
        ...blankTurn(),
        tasks: Array.isArray(latest.tasks) ? latest.tasks.length : 0,
        atLabel: whenLabel(latest.created_at, nowMs),
        sentAt: latest.created_at,
        statusLabel: ownerCommandStatusLabel('answered'),
      });
      continue;
    }

    // The desk speaks as the Overseer, so it answers only a command sent to the Overseer.
    const overseerTalks = desk.overseerReplies?.has(command.id) === true;
    const canonicalRoadmap = roadmapReadQuestion(command.content) !== null;
    const composed = targetsOverseer && (!overseerTalks || canonicalRoadmap)
      ? answers.get(command.id) : undefined;
    if (composed !== undefined) {
      turns.push({
        id: `${command.id}:answer`, kind: 'answer', mine: false, text: composed,
        ...blankTurn(),
        statusLabel: ownerCommandStatusLabel('answered'),
      });
      continue;
    }

    const drafted = commandPlans
      .filter((plan) => !wasAnswered(plan))
      .sort((a, b) => (Date.parse(a.created_at) || 0) - (Date.parse(b.created_at) || 0));

    if (drafted.length === 0) {
      if (command.project_id === null && targetsOverseer && !overseerTalks) {
        turns.push({
          id: `${command.id}:note`, kind: 'note', mine: false,
          text: 'Kept as a note. Organization-wide commands are not turned into plans.',
          ...blankTurn(),
        });
      }
      continue;
    }

    for (const plan of drafted) {
      const archivedReason = desk.archivedPlans?.get(plan.id);
      const planPhase = ownerCommandPhase(command, [plan]);
      const outcome = desk.outcomes.get(plan.id) ?? null;
      let label = ownerCommandStatusLabel(planPhase);
      if (planPhase === 'approved' && onlyQueued(outcome)) label = APPROVED_QUEUED_LABEL;
      const offer = archivedReason !== undefined ? null : planDecisionOffer(plan, desk.context, command.content);
      let text = planPhase === 'no_work' ? noWorkLine(plan) : planLine(plan, nowMs, (offer?.choices?.length ?? 0) > 0);
      if (archivedReason !== undefined) {
        label = 'archived';
        text = proposalArchiveTranscript(plan, archivedReason);
      }
      turns.push({
        id: plan.id, kind: 'plan', mine: false, text, seq: null,
        status: plan.status,
        reason: plan.status === 'failed' ? plan.reason : null,
        tasks: Array.isArray(plan.tasks) ? plan.tasks.length : null,
        atLabel: whenLabel(plan.created_at, nowMs),
        sentAt: plan.created_at,
        offer,
        outcome,
        statusLabel: label,
      });
    }
  }
  return turns;
}
